import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import Pagination from '@mui/material/Pagination'
import TextField from '@mui/material/TextField'
import Avatar from '@mui/material/Avatar'
import { Button } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import FilterListIcon from '@mui/icons-material/FilterList'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import PeopleIcon from '@mui/icons-material/People'
import Form from 'react-bootstrap/Form'
import ApiCaller from '../../constants/ApiCaller'
import urls from '../../constants/apiurl'
import { showDangerToast, showToast } from '../../constants/toaster'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import TableViewIcon from '@mui/icons-material/TableView'
import {
  updateTopHeader,
  uplodateLoading
} from '../../../redux/features/common/commonSlice'
import NoDataImg from '../../../assets/img/no-data.gif'
import './GradeUsersList.css'

const DEFAULT_PAGE_SIZE = 20
const MAX_PAGE_SIZE = 100
const EXPORT_FETCH_SIZE = 100

const formatExportDate = () => {
  const d = new Date()
  return d.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const sanitizeFilePart = (str) =>
  String(str || 'grade')
    .replace(/[^a-zA-Z0-9-_]/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 40)

const isApiSuccess = (response) =>
  response?.successful === true ||
  response?.status === 200 ||
  response?.status === '200'

const getGradeLabel = (grade) =>
  grade?.gradeName || grade?.name || `Grade #${grade?.id}`

const matchesSearch = (user, query) => {
  if (!query.trim()) return true
  const q = query.trim().toLowerCase()
  const fields = [
    user.userName,
    user.fullName,
    user.email,
    user.contactNumber
  ]
  return fields.some((f) => String(f || '').toLowerCase().includes(q))
}

export const GradeUsersList = () => {
  const { gradeId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const userData = useSelector((state) => state?.userData)
  const LOADING_ENABLE = useSelector((state) => state?.loading)

  const adminHeaders = useMemo(() => {
    const userName =
      userData?.email || userData?.emailId || userData?.userName || ''
    return userName ? { userName } : {}
  }, [userData?.email, userData?.emailId, userData?.userName])

  const [grades, setGrades] = useState([])
  const [users, setUsers] = useState([])
  const [errorMessage, setErrorMessage] = useState('')
  const [searchText, setSearchText] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageInput, setPageInput] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [pageSizeInput, setPageSizeInput] = useState(String(DEFAULT_PAGE_SIZE))
  const [exporting, setExporting] = useState(false)
  const [pagination, setPagination] = useState({
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalItems: 0
  })

  const selectedGrade = useMemo(
    () => grades.find((g) => String(g.id) === String(gradeId)),
    [grades, gradeId]
  )

  const displayGradeTitle = useMemo(() => {
    if (selectedGrade) return getGradeLabel(selectedGrade)
    return location.state?.gradeName || (gradeId ? `Grade #${gradeId}` : 'Grade')
  }, [selectedGrade, location.state?.gradeName, gradeId])

  useEffect(() => {
    dispatch(updateTopHeader(`Users — ${displayGradeTitle}`))
  }, [dispatch, displayGradeTitle])

  const fetchGrades = useCallback(async () => {
    try {
      const res = await ApiCaller.post(
        {},
        urls.gradeList,
        userData?.access_token,
        adminHeaders
      )
      const response = res?.response ? await res.response.json() : null
      if (isApiSuccess(response) && response?.data !== null) {
        const list = Array.isArray(response.data)
          ? response.data
          : response.data?.content || []
        setGrades(list)
      }
    } catch (error) {
      console.error('Failed to load grades:', error)
    }
  }, [adminHeaders, userData?.access_token])

  const fetchUsersByGrade = useCallback(
    async (pageIndex = 0, pageSize = DEFAULT_PAGE_SIZE) => {
      if (!gradeId) return

      try {
        dispatch(uplodateLoading(true))
        setErrorMessage('')

        const path = `${urls.usersByGrade}/${gradeId}`
        const res = await ApiCaller.get(
          { page: pageIndex, size: pageSize },
          path,
          userData?.access_token,
          adminHeaders
        )
        const response = res?.response ? await res.response.json() : null

        if (!response) {
          setUsers([])
          setErrorMessage('Failed to load users')
          return
        }

        if (response.data === null) {
          setUsers([])
          setPagination({
            page: pageIndex,
            size: pageSize,
            totalPages: 0,
            totalItems: 0
          })
          setErrorMessage(response.message || 'Failed to load users')
          return
        }

        if (isApiSuccess(response)) {
          const pageData = response.data || {}
          setUsers(pageData.items || [])
          setPagination({
            page: pageData.pageNumber ?? pageIndex,
            size: pageData.pageSize ?? pageSize,
            totalPages: pageData.totalPages ?? 0,
            totalItems: pageData.totalItems ?? 0
          })
          if ((pageData.items || []).length === 0 && response.message) {
            setErrorMessage(response.message)
          }
        } else {
          setUsers([])
          setErrorMessage(response.message || 'Failed to load users')
        }
      } catch (error) {
        console.error(error)
        setUsers([])
        setErrorMessage(error?.message || 'Failed to load users')
        showDangerToast(error?.message || 'Failed to load users')
      } finally {
        dispatch(uplodateLoading(false))
      }
    },
    [adminHeaders, dispatch, gradeId, userData?.access_token]
  )

  useEffect(() => {
    fetchGrades()
  }, [fetchGrades])

  useEffect(() => {
    setPage(1)
    setPageInput(1)
    setSearchText('')
    setAppliedSearch('')
    fetchUsersByGrade(0, pageSize)
  }, [gradeId, fetchUsersByGrade, pageSize])

  useEffect(() => {
    setPageSizeInput(String(pageSize))
  }, [pageSize])

  const filteredUsers = useMemo(
    () => users.filter((u) => matchesSearch(u, appliedSearch)),
    [users, appliedSearch]
  )

  const handleGradeChange = (e) => {
    const newGradeId = e.target.value
    if (!newGradeId || String(newGradeId) === String(gradeId)) return
    const grade = grades.find((g) => String(g.id) === String(newGradeId))
    navigate(`/users-by-grade/${newGradeId}`, {
      state: { gradeName: getGradeLabel(grade) },
      replace: false
    })
  }

  const handleApplyFilter = () => {
    setAppliedSearch(searchText.trim())
  }

  const handleResetFilter = () => {
    setSearchText('')
    setAppliedSearch('')
    setPage(1)
    setPageInput(1)
    fetchUsersByGrade(0, pageSize)
  }

  const handlePageSizeSubmit = () => {
    let val = Number(pageSizeInput)
    if (val < 1 || isNaN(val)) val = DEFAULT_PAGE_SIZE
    else if (val > MAX_PAGE_SIZE) val = MAX_PAGE_SIZE
    setPageSizeInput(String(val))
    if (val !== pageSize) {
      setPageSize(val)
      setPage(1)
      setPageInput(1)
      setAppliedSearch('')
      setSearchText('')
    }
  }

  const fetchAllUsersForExport = useCallback(async () => {
    if (!gradeId) return []
    const allUsers = []
    let pageIndex = 0
    let totalPages = 1

    do {
      const path = `${urls.usersByGrade}/${gradeId}`
      const res = await ApiCaller.get(
        { page: pageIndex, size: EXPORT_FETCH_SIZE },
        path,
        userData?.access_token,
        adminHeaders
      )
      const response = res?.response ? await res.response.json() : null
      if (!isApiSuccess(response) || response?.data === null) {
        throw new Error(response?.message || 'Failed to fetch users for export')
      }
      const pageData = response.data || {}
      allUsers.push(...(pageData.items || []))
      totalPages = pageData.totalPages ?? 1
      pageIndex += 1
    } while (pageIndex < totalPages)

    return allUsers
  }, [adminHeaders, gradeId, userData?.access_token])

  const buildExportRows = (userList) => {
    const exportDate = formatExportDate()
    const gradeLabel = displayGradeTitle
    return userList.map((user, idx) => ({
      'Export Date': exportDate,
      'Grade Name': gradeLabel,
      'Grade ID': gradeId,
      'S.No': idx + 1,
      'User ID': user.id,
      Username: user.userName,
      'Full Name': user.fullName,
      Email: user.email,
      Phone: user.contactNumber,
      'Profile Pic': user.profilePic || ''
    }))
  }

  const downloadWorkbook = (rows, bookType, extension) => {
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Grade Users')
    const stamp = new Date().toISOString().slice(0, 10)
    const fileName = `GradeUsers_${sanitizeFilePart(displayGradeTitle)}_${stamp}.${extension}`

    if (bookType === 'csv') {
      const csv = XLSX.utils.sheet_to_csv(ws)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      saveAs(blob, fileName)
    } else {
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([wbout], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      saveAs(blob, fileName)
    }
  }

  const handleExport = async (format) => {
    if (!gradeId) {
      showDangerToast('Select a grade first')
      return
    }
    try {
      setExporting(true)
      dispatch(uplodateLoading(true))
      let userList = await fetchAllUsersForExport()
      if (appliedSearch) {
        userList = userList.filter((u) => matchesSearch(u, appliedSearch))
      }
      if (!userList.length) {
        showDangerToast('No users to export')
        return
      }
      const rows = buildExportRows(userList)
      if (format === 'csv') {
        downloadWorkbook(rows, 'csv', 'csv')
        showToast(`Exported ${userList.length} users as CSV`)
      } else {
        downloadWorkbook(rows, 'xlsx', 'xlsx')
        showToast(`Exported ${userList.length} users as Excel`)
      }
    } catch (error) {
      console.error(error)
      showDangerToast(error?.message || 'Export failed')
    } finally {
      setExporting(false)
      dispatch(uplodateLoading(false))
    }
  }

  const handlePageChange = (_e, value) => {
    setPage(value)
    setPageInput(value)
    setAppliedSearch('')
    setSearchText('')
    fetchUsersByGrade(value - 1, pageSize)
  }

  const handlePageInputSubmit = () => {
    const value = Number(pageInput)
    const totalPages = pagination.totalPages || 1
    if (value >= 1 && value <= totalPages) {
      handlePageChange(null, value)
    } else {
      setPageInput(page)
    }
  }

  const handleRowClick = (userId) => {
    if (userId) navigate(`/user-manager/${userId}`)
  }

  const hasActiveFilter = Boolean(appliedSearch)
  const showTable = filteredUsers.length > 0
  const showEmpty =
    !LOADING_ENABLE &&
    users.length === 0 &&
    !errorMessage
  const showNoMatch =
    !LOADING_ENABLE &&
    users.length > 0 &&
    filteredUsers.length === 0

  return (
    <div className='table-responsive'>
      <div className='grade-users-toolbar'>
        <div className='d-flex align-items-center justify-content-between flex-wrap gap-2'>
          <div className='d-flex align-items-center gap-2'>
            <PeopleIcon sx={{ fontSize: 32, opacity: 0.9 }} />
            <div>
              <h4 className='mb-0 text-white'>Users by Grade</h4>
              <small style={{ opacity: 0.85 }}>
                Manage and browse users assigned to a grade
              </small>
            </div>
          </div>
          <Button
            variant='outlined'
            size='small'
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/grade-manager')}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.6)',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            Back to Grades
          </Button>
        </div>

        <div className='grade-users-stats'>
          <div className='grade-users-stat'>
            <div className='grade-users-stat-label'>Grade</div>
            <div className='grade-users-stat-value'>{displayGradeTitle}</div>
          </div>
          <div className='grade-users-stat'>
            <div className='grade-users-stat-label'>Grade ID</div>
            <div className='grade-users-stat-value'>{gradeId || '—'}</div>
          </div>
          <div className='grade-users-stat'>
            <div className='grade-users-stat-label'>Total users</div>
            <div className='grade-users-stat-value'>
              {pagination.totalItems}
            </div>
          </div>
          <div className='grade-users-stat'>
            <div className='grade-users-stat-label'>Page</div>
            <div className='grade-users-stat-value'>
              {(pagination.page ?? 0) + 1} / {pagination.totalPages || 1}
            </div>
          </div>
          {hasActiveFilter && (
            <div className='grade-users-stat'>
              <div className='grade-users-stat-label'>Showing (filtered)</div>
              <div className='grade-users-stat-value'>
                {filteredUsers.length} on this page
              </div>
            </div>
          )}
          {selectedGrade?.gradeValue != null && (
            <div className='grade-users-stat'>
              <div className='grade-users-stat-label'>Grade value</div>
              <div className='grade-users-stat-value'>
                {selectedGrade.gradeValue}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='grade-users-filters'>
        <div className='grade-users-filters-toolbar'>
          <div className='grade-users-filters-grid'>
            <div className='grade-users-field'>
              <Form.Label>Grade</Form.Label>
              <Form.Control
                as='select'
                value={gradeId || ''}
                onChange={handleGradeChange}
              >
                <option value=''>-- Select Grade --</option>
                {grades.map((g) => (
                  <option key={g.id} value={g.id}>
                    {getGradeLabel(g)}
                  </option>
                ))}
              </Form.Control>
            </div>

            <div className='grade-users-field'>
              <Form.Label>Search users</Form.Label>
              <Form.Control
                type='text'
                placeholder='Username, full name, email, or phone'
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleApplyFilter()}
              />
            </div>

            <div className='grade-users-filters-actions'>
              <Button
                variant='contained'
                color='primary'
                size='medium'
                startIcon={<FilterListIcon />}
                onClick={handleApplyFilter}
              >
                Apply
              </Button>
              <Button
                variant='outlined'
                color='secondary'
                size='medium'
                startIcon={<RestartAltIcon />}
                onClick={handleResetFilter}
              >
                Reset
              </Button>
            </div>
          </div>

          <div className='grade-users-export-actions'>
            <Button
              variant='contained'
              color='success'
              size='medium'
              startIcon={<TableViewIcon />}
              disabled={exporting || !gradeId || pagination.totalItems === 0}
              onClick={() => handleExport('xlsx')}
            >
              Export Excel
            </Button>
            <Button
              variant='outlined'
              color='success'
              size='medium'
              startIcon={<FileDownloadIcon />}
              disabled={exporting || !gradeId || pagination.totalItems === 0}
              onClick={() => handleExport('csv')}
            >
              Export CSV
            </Button>
          </div>
        </div>

        {hasActiveFilter && (
          <p className='grade-users-filter-note mb-0'>
            Filter active: &quot;{appliedSearch}&quot; — matches users on the
            current page only
          </p>
        )}
      </div>

      {errorMessage && (
        <div className='px-4 pb-2 pt-2'>
          <div className='alert alert-warning mb-0' role='alert'>
            {errorMessage}
          </div>
        </div>
      )}

      {showTable ? (
        <>
          <table className='table table-hover align-items-center table-flush table-striped mb-0'>
            <thead className='thead-dark text-white'>
              <tr className='text-white text-center'>
                <th className='text-white text-center'>S.No</th>
                <th className='text-white text-center'>Avatar</th>
                <th className='text-white text-center'>Username</th>
                <th className='text-white text-center'>Full name</th>
                <th className='text-white text-center'>Email</th>
                <th className='text-white text-center'>Phone</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user, idx) => (
                <tr
                  key={user.id}
                  className='text-center'
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleRowClick(user.id)}
                  title='View user profile'
                >
                  <td>{pagination.page * pagination.size + idx + 1}</td>
                  <td>
                    <Avatar
                      src={user.profilePic || undefined}
                      alt={user.userName}
                      sx={{ width: 40, height: 40, mx: 'auto' }}
                    >
                      {(user.fullName || user.userName || '?')
                        .charAt(0)
                        .toUpperCase()}
                    </Avatar>
                  </td>
                  <td className='text-primary'>{user.userName}</td>
                  <td>{user.fullName}</td>
                  <td>{user.email}</td>
                  <td>{user.contactNumber}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className='card-footer py-4 grade-users-footer'>
            <span className='grade-users-footer-info'>
              Page {pagination.page + 1} of {pagination.totalPages || 1} —{' '}
              {pagination.totalItems} total users ({pageSize} per page)
            </span>
            <div className='grade-users-footer-controls'>
              <TextField
                label='Users per page'
                type='number'
                inputProps={{ min: 1, max: MAX_PAGE_SIZE }}
                value={pageSizeInput}
                onChange={(e) => setPageSizeInput(e.target.value)}
                onBlur={handlePageSizeSubmit}
                onKeyDown={(e) =>
                  e.key === 'Enter' && handlePageSizeSubmit()
                }
                size='small'
                sx={{ width: 130 }}
              />
              <TextField
                label='Go to page'
                type='number'
                inputProps={{ min: 1, max: pagination.totalPages || 1 }}
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onBlur={handlePageInputSubmit}
                onKeyDown={(e) =>
                  e.key === 'Enter' && handlePageInputSubmit()
                }
                size='small'
                sx={{ width: 120 }}
              />
              <Pagination
                count={pagination.totalPages || 1}
                page={page}
                onChange={handlePageChange}
                color='secondary'
              />
            </div>
          </div>
        </>
      ) : showNoMatch ? (
        <div className='text-center py-4'>
          <img width='25%' src={NoDataImg} alt='No match' />
          <br />
          <label style={{ fontWeight: 'bold' }}>
            No users match &quot;{appliedSearch}&quot; on this page
          </label>
          <br />
          <Button
            variant='link'
            size='small'
            onClick={handleResetFilter}
            className='mt-2'
          >
            Clear filter
          </Button>
        </div>
      ) : showEmpty ? (
        <div className='text-center py-4'>
          <img width='25%' src={NoDataImg} alt='No users' />
          <br />
          <label style={{ fontWeight: 'bold' }}>
            No users found for this grade
          </label>
        </div>
      ) : (
        <div className='text-center py-4'>
          <img width='25%' src={NoDataImg} alt='Loading' />
          <br />
          <label style={{ fontWeight: 'bold' }}>
            {LOADING_ENABLE ? 'Loading users...' : 'No Data Found'}
          </label>
        </div>
      )}
    </div>
  )
}

export default GradeUsersList
